import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, ChevronUp, ChevronDown, Users, Search, Settings } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useFloatingChat } from '@/hooks/useFloatingChat';
import { useWebSocket } from '@/hooks/useWebSocket';
import { ChatSettings } from './ChatSettings';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ChatMessage {
  id: string;
  content: string;
  sender: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  createdAt: string;
  isEdited: boolean;
  replyTo?: {
    id: string;
    content: string;
    sender: {
      id: string;
      name: string;
    };
  };
}

interface ChatRoom {
  id: string;
  name: string;
  type: 'DIRECT' | 'GROUP' | 'SUPPORT';
  participants: Array<{
    user: {
      id: string;
      name: string;
      email: string;
      role: string;
    };
  }>;
  _count: {
    messages: number;
  };
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

export const FloatingChat: React.FC = () => {
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showRooms, setShowRooms] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomType, setNewRoomType] = useState<'DIRECT' | 'GROUP' | 'SUPPORT'>('DIRECT');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [chatSettings, setChatSettings] = useState({
    soundEnabled: true,
    notificationsEnabled: true,
    darkMode: false,
    autoOpen: false,
    showTypingIndicator: true,
  });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  
  // Hook personalizado para gerenciar estado do chat
  const {
    isOpen,
    isMinimized,
    unreadCount,
    toggleChat,
    toggleMinimize,
    closeChat,
    selectRoom,
    updateUnreadCount,
    resetUnreadCount,
  } = useFloatingChat();

  // Hook para WebSocket
  const { joinRoom, leaveRoom, sendTyping, onMessage, isConnected } = useWebSocket();

  // Buscar salas do usuário
  const { data: rooms = [] } = useQuery({
    queryKey: ['chat-rooms'],
    queryFn: async () => {
      const response = await api.get('/internal-chat/rooms');
      return response.data.data;
    },
    enabled: isOpen,
  });

  // Buscar usuários disponíveis
  const { data: availableUsers = [] } = useQuery({
    queryKey: ['chat-users'],
    queryFn: async () => {
      const response = await api.get('/internal-chat/users');
      return response.data.data;
    },
    enabled: isOpen && showUsers,
  });

  // Buscar mensagens da sala selecionada
  const { data: messagesData } = useQuery({
    queryKey: ['chat-messages', selectedRoom?.id],
    queryFn: async () => {
      if (!selectedRoom) return { messages: [], total: 0 };
      const response = await api.get(`/internal-chat/rooms/${selectedRoom.id}/messages?limit=50`);
      return response.data.data;
    },
    enabled: !!selectedRoom,
    refetchInterval: 5000, // Reduzir para 5 segundos para economizar recursos
    staleTime: 3000, // Considerar dados frescos por 3 segundos
  });

  // Enviar mensagem
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!selectedRoom) throw new Error('Nenhuma sala selecionada');
      const response = await api.post('/internal-chat/messages', {
        roomId: selectedRoom.id,
        content,
        type: 'TEXT',
      });
      return response.data.data;
    },
    onSuccess: () => {
      setMessage('');
      queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedRoom?.id] });
    },
  });

  // Criar nova sala
  const createRoomMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/internal-chat/rooms', {
        name: newRoomName,
        type: newRoomType,
        participants: selectedUsers,
      });
      return response.data.data;
    },
    onSuccess: (newRoom) => {
      setNewRoomName('');
      setSelectedUsers([]);
      setShowUsers(false);
      setSelectedRoom(newRoom);
      queryClient.invalidateQueries({ queryKey: ['chat-rooms'] });
    },
  });

  // Marcar mensagens como lidas com debounce
  const markAsReadMutation = useMutation({
    mutationFn: async (roomId: string) => {
      try {
        await api.patch(`/internal-chat/rooms/${roomId}/read`);
      } catch (error: any) {
        // Log do erro para debug
        console.warn('Erro ao marcar mensagens como lidas:', error);
        
        // Se for erro de recursos insuficientes, tentar novamente após delay
        if (error.message?.includes('ERR_INSUFFICIENT_RESOURCES')) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          return await api.patch(`/internal-chat/rooms/${roomId}/read`);
        }
        
        throw error;
      }
    },
    onSuccess: () => {
      resetUnreadCount();
    },
    onError: (error: any) => {
      console.error('Erro ao marcar mensagens como lidas:', error);
    },
    retry: 1, // Limitar tentativas
    retryDelay: 1000, // Esperar 1 segundo antes de tentar novamente
  });

  // Filtrar salas baseado na busca
  const filteredRooms = rooms.filter((room: ChatRoom) =>
    room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    room.participants.some(p => 
      p.user.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Filtrar usuários baseado na busca
  const filteredUsers = availableUsers.filter((user: User) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Scroll para a última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesData?.messages]);

  // Marcar como lido quando selecionar sala (com debounce)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (selectedRoom) {
      // Debounce para evitar muitas requisições
      timeoutId = setTimeout(() => {
        markAsReadMutation.mutate(selectedRoom.id);
      }, 500);

      selectRoom(selectedRoom.id);
      
      // Entrar na sala via WebSocket
      joinRoom(selectedRoom.id);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (selectedRoom) {
        leaveRoom(selectedRoom.id);
      }
    };
  }, [selectedRoom, selectRoom, joinRoom, leaveRoom, markAsReadMutation]);

  // Focar no input quando abrir
  useEffect(() => {
    if (isOpen && !isMinimized && selectedRoom) {
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isMinimized, selectedRoom]);

  // Configurar listeners do WebSocket
  useEffect(() => {
    onMessage((message) => {
      switch (message.type) {
        case 'chat-message':
          // Atualizar mensagens em tempo real
          queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedRoom?.id] });
          
          // Notificação se não estiver na sala
          if (message.data.senderId !== user?.id && !isOpen) {
            updateUnreadCount((prev: number) => prev + 1);
            
            // Tocar som de notificação se habilitado
            if (chatSettings.soundEnabled) {
              const audio = new Audio('/notification.mp3');
              audio.play().catch(() => {
                // Ignorar erro se o navegador bloquear autoplay
              });
            }
          }
          
          // Rolar para o final das mensagens quando receber nova mensagem
          setTimeout(() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
          break;
          
        case 'typing':
          if (message.data.roomId === selectedRoom?.id) {
            if (message.data.isTyping) {
              setTypingUsers(prev => [...new Set([...prev, message.data.userName])]);
            } else {
              setTypingUsers(prev => prev.filter(name => name !== message.data.userName));
            }
          }
          break;
          
        case 'room-update':
          // Atualizar lista de salas
          queryClient.invalidateQueries({ queryKey: ['chat-rooms'] });
          break;
      }
    });
  }, [onMessage, queryClient, selectedRoom?.id, user?.id, isOpen, updateUnreadCount, chatSettings.soundEnabled]);

  // Rolar para o final das mensagens quando carregar ou trocar de sala
  useEffect(() => {
    if (messagesEndRef.current && selectedRoom) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [selectedRoom, messagesData?.messages?.length]);

  const handleSendMessage = () => {
    if (message.trim() && selectedRoom) {
      sendMessageMutation.mutate(message.trim());
      
      // Rolar para o final após enviar mensagem
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
    
    // Enviar status de digitação via WebSocket
    if (selectedRoom && isConnected()) {
      sendTyping(selectedRoom.id, e.target.value.length > 0);
    }
  };

  const handleCreateRoom = () => {
    if (newRoomName.trim() && selectedUsers.length > 0) {
      createRoomMutation.mutate();
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoomDisplayName = (room: ChatRoom) => {
    if (room.type === 'DIRECT') {
      const otherParticipant = room.participants.find(p => p.user.id !== user?.id);
      return otherParticipant?.user.name || room.name;
    }
    return room.name;
  };

  const getUnreadCount = (_room: ChatRoom) => {
    // Por enquanto, retorna um valor simulado
    // TODO: Implementar lógica real de contagem de mensagens não lidas
    return Math.floor(Math.random() * 5); // Simulação para demonstração
  };

  if (!user) return null;

  return (
    <>
      {/* Botão flutuante */}
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={toggleChat}
          className="w-14 h-14 rounded-full shadow-lg dark:shadow-2xl dark:shadow-slate-900/50 bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 hover:scale-105"
        >
          <MessageCircle size={24} />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs animate-pulse">
              {unreadCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Chat flutuante */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 w-96 h-[500px] bg-white dark:bg-slate-800 rounded-lg shadow-2xl border border-gray-200 dark:border-slate-700 z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 rounded-t-lg">
            <div className="flex items-center space-x-2">
              <MessageCircle size={20} className="text-blue-600" />
              <h3 className="font-semibold text-gray-800 dark:text-slate-200">Chat Interno</h3>
              <div className={`w-2 h-2 rounded-full ${isConnected() ? 'bg-green-500' : 'bg-red-500'}`} 
                   title={isConnected() ? 'Conectado' : 'Desconectado'} />
            </div>
            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="h-8 w-8 p-0"
                title="Configurações"
              >
                <Settings size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleMinimize}
                className="h-8 w-8 p-0"
              >
                {isMinimized ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeChat}
                className="h-8 w-8 p-0"
              >
                <X size={16} />
              </Button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Sidebar com salas */}
              {!selectedRoom && (
                <div className="flex-1 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <div className="flex items-center space-x-2 mb-3">
                      <Search size={16} className="text-gray-400 dark:text-slate-400" />
                      <Input
                        placeholder="Buscar salas ou usuários..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant={showRooms ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setShowRooms(true);
                          setShowUsers(false);
                        }}
                      >
                        Salas
                      </Button>
                      <Button
                        variant={showUsers ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setShowUsers(true);
                          setShowRooms(false);
                        }}
                      >
                        <Users size={16} className="mr-1" />
                        Usuários
                      </Button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto chat-scrollbar p-2">
                    {showRooms && (
                      <div className="space-y-2">
                        {filteredRooms.map((room: ChatRoom) => (
                          <div
                            key={room.id}
                            onClick={() => setSelectedRoom(room)}
                            className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer border border-gray-100 dark:border-slate-600"
                          >
                            <Avatar size="sm">
                              {getInitials(getRoomDisplayName(room))}
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="font-medium text-sm text-gray-900 dark:text-slate-200 truncate">
                                  {getRoomDisplayName(room)}
                                </p>
                                {getUnreadCount(room) > 0 && (
                                  <Badge className="bg-blue-500 text-white text-xs">
                                    {getUnreadCount(room)}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-slate-400">
                                {room.participants.length} participantes
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {showUsers && (
                      <div className="flex flex-col h-full">
                        <div className="flex-shrink-0 p-3 border border-gray-200 dark:border-slate-600 rounded-lg mb-3 bg-white dark:bg-slate-800">
                          <h4 className="font-medium text-sm mb-3 text-gray-900 dark:text-slate-200">Criar Nova Sala</h4>
                          <Input
                            placeholder="Nome da sala"
                            value={newRoomName}
                            onChange={(e) => setNewRoomName(e.target.value)}
                            className="mb-2"
                          />
                          <select
                            value={newRoomType}
                            onChange={(e) => setNewRoomType(e.target.value as any)}
                            className="w-full p-2 border border-gray-300 dark:border-slate-600 rounded-md mb-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-200"
                          >
                            <option value="DIRECT">Chat Direto</option>
                            <option value="GROUP">Grupo</option>
                            <option value="SUPPORT">Suporte</option>
                          </select>
                          <Button
                            onClick={handleCreateRoom}
                            disabled={!newRoomName.trim() || selectedUsers.length === 0}
                            className="w-full"
                          >
                            Criar Sala
                          </Button>
                        </div>

                        <div className="flex-1 overflow-y-auto chat-scrollbar space-y-2">
                          {filteredUsers.map((user: User) => (
                            <div
                              key={user.id}
                              onClick={() => toggleUserSelection(user.id)}
                              className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer border ${
                                selectedUsers.includes(user.id)
                                  ? 'bg-blue-50 border-blue-200'
                                  : 'hover:bg-gray-50 dark:hover:bg-slate-700 border-gray-100 dark:border-slate-600'
                              }`}
                            >
                              <Avatar size="sm">
                                {getInitials(user.name)}
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900 dark:text-slate-200 truncate">
                                  {user.name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                  {user.email}
                                </p>
                              </div>
                              {selectedUsers.includes(user.id) && (
                                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                  <div className="w-2 h-2 bg-white dark:bg-slate-800 rounded-full" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Área de chat */}
              {selectedRoom && (
                <div className="flex-1 flex flex-col">
                  {/* Header da sala */}
                  <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 dark:bg-slate-800">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedRoom(null)}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronUp size={16} className="rotate-90" />
                      </Button>
                      <Avatar size="sm">
                        {getInitials(getRoomDisplayName(selectedRoom))}
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-slate-200">
                          {getRoomDisplayName(selectedRoom)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          {selectedRoom.participants.length} participantes
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Mensagens */}
                  <div className="flex-1 overflow-y-auto chat-scrollbar p-3 space-y-3">
                    {messagesData?.messages?.map((msg: ChatMessage) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender.id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            msg.sender.id === user?.id
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-slate-200'
                          }`}
                        >
                          {msg.replyTo && (
                            <div className={`text-xs mb-1 ${msg.sender.id === user?.id ? 'text-blue-100' : 'text-gray-500 dark:text-slate-400'}`}>
                              Respondendo a {msg.replyTo.sender.name}: {msg.replyTo.content.substring(0, 30)}...
                            </div>
                          )}
                          <p className="text-sm">{msg.content}</p>
                          <div className={`flex items-center justify-between mt-1 text-xs ${
                            msg.sender.id === user?.id ? 'text-blue-100' : 'text-gray-500 dark:text-slate-400'
                          }`}>
                            <span>{msg.sender.name}</span>
                            <span>{formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true, locale: ptBR })}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                                     {/* Input de mensagem */}
                   <div className="p-3 border-t border-gray-200 dark:border-slate-700">
                     <div className="flex space-x-2">
                                               <Input
                          ref={messageInputRef}
                          placeholder="Digite sua mensagem..."
                          value={message}
                          onChange={handleTyping}
                          onKeyPress={handleKeyPress}
                          onFocus={() => setIsTyping(true)}
                          onBlur={() => {
                            setIsTyping(false);
                            if (selectedRoom && isConnected()) {
                              sendTyping(selectedRoom.id, false);
                            }
                          }}
                          className="flex-1"
                        />
                       <Button
                         onClick={handleSendMessage}
                         disabled={!message.trim() || sendMessageMutation.isPending}
                         className="px-4"
                       >
                         <Send size={16} />
                       </Button>
                     </div>
                                           {(isTyping || typingUsers.length > 0) && chatSettings.showTypingIndicator && (
                        <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          {typingUsers.length > 0 
                            ? `${typingUsers.join(', ')} ${typingUsers.length === 1 ? 'está' : 'estão'} digitando...`
                            : 'Digitando...'
                          }
                        </div>
                      )}
                   </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modal de Configurações */}
      <ChatSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={chatSettings}
        onSettingsChange={setChatSettings}
      />
    </>
  );
};
